import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import useAuthStore from "./store/authStore";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./components/DashboardLayout";
import ConsentBanner from "./components/ConsentBanner";
import StatusPage from "./pages/StatusPage";
import ResumePayment from "./pages/ResumePayment";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Orders from "./pages/Orders";
import Tracking from "./pages/Tracking";
import Agents from "./pages/Agents";
import Earnings from "./pages/Earnings";
import Analytics from "./pages/Analytics";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailed from "./pages/PaymentFailed";
import AdminCustomers from "./pages/AdminCustomers";
import DebugRole from "./pages/DebugRole";
import ApprovalsQueue from "./pages/ApprovalsQueue";
import AgentTasks from "./pages/agent/AgentTasks";
import RoleGate from "./components/RoleGate";
import AgentEarnings from "./pages/agent/Earnings";
import MicroHubOrders from "./pages/hub/MicroHubOrders";
import MainHubOrders from "./pages/hub/MainHubOrders";
import MainHubDashboard from "./pages/hub/MainHubDashboard";
import InboundParcels from "./pages/hub/InboundParcels";
import MicroHubs from "./pages/hub/MicroHubs";
import HubAgents from "./pages/hub/HubAgents";
import DispatchCenter from "./pages/hub/DispatchCenter";
import ManifestDetail from "./pages/hub/ManifestDetail";
import OpsLiveMap from "./pages/hub/OpsLiveMap";
import HubFinance from "./pages/hub/HubFinance";
import SupportTickets from "./pages/hub/SupportTickets";
import Inventory from "./pages/hub/Inventory";
import HubAnalytics from "./pages/hub/HubAnalytics";
import CourierPartners from "./pages/hub/CourierPartners";
import OrderDetailPage from "./pages/hub/OrderDetailPage";
import HubSettings from "./pages/hub/HubSettings";
import AdminApprovals from "./pages/AdminApprovals";
import {
  MicroHubLeadDashboard, MicroHubLeadProfile, MicroHubLeadOrders,
  MicroHubLeadInventory, MicroHubLeadTickets,
  MicroHubOrderNotFound,
} from "./pages/micro_hub/MicroHubLeadPages";
import MicroHubWorkers from "./pages/micro_hub/MicroHubWorkers";
// Phase HUB-DRILL-1
import MainHubsList         from "./pages/founder/MainHubsList";
import MicroHubsList        from "./pages/founder/MicroHubsList";
import CampusHubsList       from "./pages/founder/CampusHubsList";
import MainHubDetailPage    from "./pages/founder/MainHubDetailPage";
import MicroHubDetailPage   from "./pages/founder/MicroHubDetailPage";
// Phase APP-3 — Founder oversight UI
import FounderOversight     from "./pages/founder/FounderOversight";
// Phase APP-4 — MHM packer approvals · MHM my suggestions · Lead worker requests · Packer pending splash
import MHMPackerApprovals   from "./pages/hub/MHMPackerApprovals";
import MHMMySuggestions     from "./pages/hub/MHMMySuggestions";
import LeadWorkerRequests   from "./pages/microlead/LeadWorkerRequests";
import PackerPendingSplash  from "./pages/packer/PackerPendingSplash";
// Phase COLLEGE-PRICING-1 — public + CMS
import CollegeLogisticsPricing from "./pages/college/CollegeLogisticsPricing";
import CollegePricingCMS    from "./pages/admin/CollegePricingCMS";
import AdminSystem          from "./pages/admin/AdminSystem";
import AdminAnalytics       from "./pages/admin/AdminAnalytics";
// Phase APP-2
import AdminApprovalsApp2 from "./pages/admin/AdminApprovals";
import PackerDashboard from "./pages/packer/PackerDashboard";
import PackerParcelDetail from "./pages/packer/PackerParcelDetail";
import { OrderDrawerProvider } from "./components/hub/OrderDrawerContext";
import OrderDetailDrawer from "./components/hub/OrderDetailDrawer";
import { ThemeProvider } from "./contexts/ThemeContext";
import AdminAgentDetail from "./pages/AdminAgentDetail";
import AdminCustomerDetail from "./pages/AdminCustomerDetail";
import AdminRevenue from "./pages/AdminRevenue";
import AdminLiveOps from "./pages/AdminLiveOps";
import AdminPricing from "./pages/AdminPricing";
import AdminPricingHealth from "./pages/AdminPricingHealth";
import AdminEditRequests from "./pages/AdminEditRequests";
import AdminCoupons from "./pages/AdminCoupons";
import AdminCancellations from "./pages/AdminCancellations";
import AdminBroadcast from "./pages/AdminBroadcast";
import AdminLogin from "./pages/AdminLogin";
import AdminReports from "./pages/AdminReports";
import AdminPayouts from "./pages/AdminPayouts";
import AdminRefundsLifecycle from "./pages/AdminRefundsLifecycle";
import AdminHubs from "./pages/AdminHubs";
import AdminHubPricing from "./pages/AdminHubPricing";
import AdminStats from "./pages/AdminStats";
import AdminSystemHealth from "./pages/AdminSystemHealth";
import WalletPage from "./pages/Wallet";
import Referral from "./pages/Referral";
import Loyalty from "./pages/Loyalty";
import Leaderboard from "./pages/Leaderboard";
import AdminCampaigns from "./pages/AdminCampaigns";
// Sprint 77 — AI Layer
import AdminAnomalies from "./pages/AdminAnomalies";
import AdminHubSuggestions from "./pages/AdminHubSuggestions";
import AdminSupportTickets from "./pages/AdminSupportTickets";
import AdminCities from "./pages/AdminCities";
import CityDetail from "./pages/CityDetail";
import ProductionSetup from "./pages/ProductionSetup";
import Help from "./pages/Help";
import CustomerSupport from "./pages/CustomerSupport";
import OrderTrackingDetail from "./pages/OrderTrackingDetail";
import SupportTicketDetail from "./pages/SupportTicketDetail";
import B2BLanding from "./pages/B2BLanding";
import B2BLogin from "./pages/B2BLogin";
import B2BRegister from "./pages/B2BRegister";
import B2BTracking from "./pages/B2BTracking";
import B2BDashboard from "./pages/B2BDashboard";
import AdminB2BPricing from "./pages/AdminB2BPricing";
import BecomeAgent from "./pages/BecomeAgent";
import BecomeAgentApply from "./pages/BecomeAgentApply";
import AdminAgentApplications from "./pages/AdminAgentApplications";
import AdminGigCommission from "./pages/AdminGigCommission";
import AdminDamageClaims from "./pages/AdminDamageClaims";
import AdminKPIs from "./pages/AdminKPIs";
import AdminCouriers from "./pages/AdminCouriers";
import CourierShipmentsList from "./pages/courier/CourierShipmentsList";
import PublicTrackingPage from "./pages/track/PublicTrackingPage";
import AdminSurge from "./pages/AdminSurge";
import AdminTargetBroadcast from "./pages/AdminTargetBroadcast";
import AdminWelcomeKits from "./pages/AdminWelcomeKits";
import AdminServiceTypes from "./pages/AdminServiceTypes";
import AdminContactSettings from "./pages/AdminContactSettings";
import AdminContacts from "./pages/AdminContacts";
import AgentDispatchFeed from "./pages/AgentDispatchFeed";
import CollegeLanding from "./pages/CollegeLanding";
import Shopify from "./pages/Shopify";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import HomeSection from "./pages/HomeSection";
import FounderDashboard from "./pages/FounderDashboard";
import ManagerOnboarding from "./pages/admin/ManagerOnboarding";
import ChainOfCustody from "./pages/admin/ChainOfCustody";
import AcceptInvite from "./pages/AcceptInvite";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import CEODashboard from "./pages/CEODashboard";
import LegacyMainHubDashboard from "./pages/MainHubDashboard";
import MicroHubDashboard from "./pages/MicroHubDashboard";
import OpsStaffDashboard from "./pages/OpsStaffDashboard";
import PublicTracking from "./pages/PublicTracking";
import PasswordReset from "./pages/PasswordReset";
import B2BContracts from "./pages/B2BContracts";
import AdminFeedback from "./pages/AdminFeedback";
import AdminAudit from "./pages/AdminAudit";
import AdminSiteSettings from "./pages/AdminSiteSettings";
import AdminServiceability from "./pages/AdminServiceability";
import MobilePhotoUpload from "./pages/MobilePhotoUpload";
import RootErrorBoundary from "./components/RootErrorBoundary";
import VersionWatcher from "./components/VersionWatcher";
import { installErrorReporter } from "./utils/errorReporter";
import { GoogleOAuthProvider } from "@react-oauth/google";

// Wire global JS error capture once at module load.
installErrorReporter();

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Toaster position="top-right" richColors closeButton />
      <ConsentBanner />
      <RootErrorBoundary>
        <ThemeProvider>
        <BrowserRouter>
        <VersionWatcher />
        <OrderDrawerProvider>
        <OrderDetailDrawer />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/resume/:intent_id" element={<ResumePayment />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-failed" element={<PaymentFailed />} />
          <Route path="/b2b" element={<B2BLanding />} />
          <Route path="/b2b/login" element={<B2BLogin />} />
          <Route path="/b2b/register" element={<B2BRegister />} />
          <Route path="/b2b/track" element={<B2BTracking />} />
          <Route path="/become-agent" element={<BecomeAgent />} />
          <Route path="/become-agent/apply" element={<BecomeAgentApply />} />
          <Route path="/college" element={<CollegeLanding />} />
          <Route path="/college-logistics-pricing" element={<CollegeLogisticsPricing />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* SEO-friendly landing-section aliases — render full Home, auto-scroll
              to the relevant section. Canonicalised to / via public/index.html
              so Google consolidates ranking to the homepage. */}
          <Route path="/pricing" element={<HomeSection anchor="calculator" />} />
          <Route path="/about" element={<HomeSection anchor="how-it-works" />} />
          <Route path="/contact" element={<HomeSection anchor="contact" />} />
          {/* Public tracking — single page, PickNGo tracking ID only.
              Legacy /b2b/track kept for backward compat. */}
          <Route path="/track" element={<PublicTrackingPage />} />
          <Route path="/track/:tracking_id" element={<PublicTrackingPage />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          {/* Manager onboarding — public set-password from emailed invite link */}
          <Route path="/accept-invite" element={<AcceptInvite />} />
          {/* Forced first-login password change (manual-credential managers) */}
          <Route path="/force-password-change" element={<ProtectedRoute><ForcePasswordChange /></ProtectedRoute>} />
          {/* Sprint-83 — Mobile QR-handoff photo upload (public, no auth) */}
          <Route path="/m/upload/:sessionId" element={<MobilePhotoUpload />} />
          {/* Phase P-3 — Packer Staff Portal (mobile-first; outside the
              DashboardLayout shell so it gets its own minimal chrome). */}
          {/* Phase APP-4 — Packer pending splash (non-active packers) */}
          <Route path="/packer/pending" element={<ProtectedRoute><PackerPendingSplash /></ProtectedRoute>} />
          <Route path="/packer/dashboard" element={<ProtectedRoute><PackerDashboard /></ProtectedRoute>} />
          <Route path="/packer/parcel/:trackingId" element={<ProtectedRoute><PackerParcelDetail /></ProtectedRoute>} />
          {/* Iteration 62 — legacy/shared link compat: /tracking → /dashboard/tracking */}
          <Route path="/tracking" element={<Navigate to="/dashboard/tracking" replace />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="orders" element={<Orders />} />
            <Route path="_debug/role" element={<DebugRole />} />
            <Route path="approvals" element={<ApprovalsQueue />} />
            <Route path="agent/tasks" element={<RoleGate allow={["agent"]}><AgentTasks /></RoleGate>} />
            <Route path="agent/earnings" element={<RoleGate allow={["agent", "admin", "founder", "ceo"]}><AgentEarnings /></RoleGate>} />
            <Route path="microhub/orders" element={<MicroHubOrders />} />
            <Route path="hub/orders" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><MainHubOrders /></RoleGate>} />
            {/* Phase MHM-1 — Main Hub Manager portal */}
            <Route path="hub/main" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><MainHubDashboard /></RoleGate>} />
            {/* Phase MHM-6.D — MHM inbound parcels screen */}
            <Route path="hub/inbound" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><InboundParcels /></RoleGate>} />
            <Route path="hub/micro-hubs" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><MicroHubs /></RoleGate>} />
            <Route path="hub/agents" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><HubAgents /></RoleGate>} />
            {/* Phase MHM-2 — Dispatch + Ops Live Map */}
            <Route path="hub/dispatch" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><DispatchCenter /></RoleGate>} />
            <Route path="hub/dispatch/:manifestId" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><ManifestDetail /></RoleGate>} />
            <Route path="hub/live-map" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><OpsLiveMap /></RoleGate>} />
            {/* Phase MHM-3 — Finance + Support + Inventory */}
            <Route path="hub/finance" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><HubFinance /></RoleGate>} />
            <Route path="hub/support" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo", "micro_hub_lead"]}><SupportTickets /></RoleGate>} />
            <Route path="hub/inventory" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo", "micro_hub_lead"]}><Inventory /></RoleGate>} />
            {/* Phase MHM-4 — Analytics + Couriers */}
            <Route path="hub/analytics" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo", "micro_hub_lead"]}><HubAnalytics /></RoleGate>} />
            <Route path="hub/couriers" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo", "micro_hub_lead"]}><CourierPartners /></RoleGate>} />
            {/* Phase DELHIVERY-1 (HYBRID) — courier shipments list (different from MHM-4 slab-pricing CMS) */}
            <Route path="hub/courier-shipments" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><CourierShipmentsList scope="hub" /></RoleGate>} />
            <Route path="admin/courier-shipments" element={<RoleGate allow={["admin", "founder", "ceo"]}><CourierShipmentsList scope="admin" /></RoleGate>} />
            {/* Phase MHM-3.5 — unified shipment-detail (drawer + standalone page) */}
            <Route path="hub/orders/:trackingId" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><OrderDetailPage /></RoleGate>} />
            <Route path="hub/settings" element={<RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}><HubSettings /></RoleGate>} />
            {/* Phase MHM-5.2 — admin approvals queue */}
            <Route path="admin/approvals" element={<RoleGate allow={["admin", "founder", "ceo"]}><AdminApprovalsApp2 /></RoleGate>} />
            {/* Phase MHM-5.3 — micro_hub_lead portal */}
            <Route path="micro-hub" element={<MicroHubLeadDashboard />} />
            <Route path="micro-hub/profile" element={<MicroHubLeadProfile />} />
            <Route path="micro-hub/orders" element={<MicroHubLeadOrders />} />
            <Route path="micro-hub/orders/:tid" element={<MicroHubOrderNotFound />} />
            <Route path="micro-hub/workers" element={<MicroHubWorkers />} />
            <Route path="micro-hub/inventory" element={<MicroHubLeadInventory />} />
            <Route path="micro-hub/tickets" element={<MicroHubLeadTickets />} />
            <Route path="orders/:trackingId" element={<OrderTrackingDetail />} />
            <Route path="tracking" element={<Tracking />} />
            <Route path="agents" element={<RoleGate allow={["admin", "founder", "ceo"]}><Agents /></RoleGate>} />
            <Route path="agents/:agentId" element={<RoleGate allow={["admin", "founder", "ceo"]}><AdminAgentDetail /></RoleGate>} />
            <Route path="earnings" element={<Earnings />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="customers" element={<RoleGate allow={["admin", "founder", "ceo"]}><AdminCustomers /></RoleGate>} />
            <Route path="customers/:customerId" element={<RoleGate allow={["admin", "founder", "ceo"]}><AdminCustomerDetail /></RoleGate>} />
            <Route path="revenue" element={<RoleGate allow={["admin", "founder", "ceo"]}><AdminRevenue /></RoleGate>} />
            <Route path="live-ops" element={<AdminLiveOps />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="pricing-health" element={<AdminPricingHealth />} />
            <Route path="edit-requests" element={<AdminEditRequests />} />
            <Route path="coupons" element={<RoleGate allow={["admin", "founder", "ceo"]}><AdminCoupons /></RoleGate>} />
            <Route path="cancellations" element={<RoleGate allow={["admin", "founder", "ceo"]}><AdminCancellations /></RoleGate>} />
            <Route path="broadcast" element={<AdminBroadcast />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="payouts" element={<RoleGate allow={["admin", "founder", "ceo"]}><AdminPayouts /></RoleGate>} />
            <Route path="refunds" element={<RoleGate allow={["admin", "founder", "ceo"]}><AdminRefundsLifecycle /></RoleGate>} />
            <Route path="hubs" element={<AdminHubs />} />
            <Route path="hub-pricing" element={<AdminHubPricing />} />
            <Route path="stats" element={<AdminStats />} />
            <Route path="system-health" element={<AdminSystemHealth />} />
            <Route path="wallet" element={<WalletPage />} />
            <Route path="referral" element={<Referral />} />
            <Route path="loyalty" element={<Loyalty />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="campaigns" element={<AdminCampaigns />} />
            <Route path="anomalies" element={<RoleGate allow={["admin", "founder", "ceo"]}><AdminAnomalies /></RoleGate>} />
            <Route path="hub-suggestions" element={<AdminHubSuggestions />} />
            <Route path="support" element={<AdminSupportTickets />} />
            <Route path="cities" element={<AdminCities />} />
            <Route path="cities/:idOrCode" element={<CityDetail />} />
            <Route path="production" element={<ProductionSetup />} />
            <Route path="help" element={<Help />} />
            <Route path="contact-us" element={<CustomerSupport />} />
            <Route path="contact-us/:ticketId" element={<CustomerSupport />} />
            <Route path="support/tickets/:ticketId" element={<SupportTicketDetail />} />
            <Route path="b2b" element={<B2BDashboard />} />
            <Route path="b2b-pricing" element={<AdminB2BPricing />} />
            <Route path="b2b-contracts" element={<B2BContracts />} />
            <Route path="agent-applications" element={<AdminAgentApplications />} />
            <Route path="gig-commission" element={<AdminGigCommission />} />
            <Route path="damage-claims" element={<AdminDamageClaims />} />
            <Route path="kpis" element={<AdminKPIs />} />
            <Route path="couriers" element={<AdminCouriers />} />
            <Route path="surge" element={<AdminSurge />} />
            <Route path="target-broadcast" element={<AdminTargetBroadcast />} />
            <Route path="welcome-kits" element={<AdminWelcomeKits />} />
            <Route path="service-types" element={<AdminServiceTypes />} />
            <Route path="contact-settings" element={<AdminContactSettings />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="dispatch" element={<AgentDispatchFeed />} />
            <Route path="shopify" element={<Shopify />} />
            {/* Sprint 70 — Operational Hierarchy dashboards */}
            <Route path="founder" element={<FounderDashboard />} />
            <Route path="ceo" element={<RoleGate allow={["founder", "ceo", "admin"]}><CEODashboard /></RoleGate>} />
            {/* Hub Manager onboarding & credential lifecycle (Founder/CEO/Admin) */}
            <Route path="managers" element={<RoleGate allow={["founder", "ceo", "admin"]}><ManagerOnboarding /></RoleGate>} />
            <Route path="chain-of-custody" element={<RoleGate allow={["founder", "ceo", "admin", "ops_staff", "main_hub_manager"]}><ChainOfCustody /></RoleGate>} />
            {/* Phase HUB-DRILL-1 — drill-down list + detail pages */}
            <Route path="founder/main-hubs"           element={<MainHubsList />} />
            <Route path="founder/main-hubs/:hubId"    element={<MainHubDetailPage />} />
            <Route path="founder/micro-hubs"          element={<MicroHubsList />} />
            <Route path="founder/micro-hubs/:hubId"   element={<MicroHubDetailPage />} />
            <Route path="founder/campus-hubs"         element={<CampusHubsList />} />
            {/* Phase APP-3 — Founder Oversight UI (admin auto-redirects to /admin/approvals from inside the page) */}
            <Route path="founder/oversight"           element={<FounderOversight />} />
            {/* Phase COLLEGE-PRICING-1 — CMS (admin + founder both mount the same page) */}
            <Route path="admin/college-pricing"       element={<CollegePricingCMS />} />
            <Route path="admin/system"                 element={<AdminSystem />} />
            <Route path="admin/analytics"              element={<AdminAnalytics />} />
            <Route path="founder/college-pricing"     element={<CollegePricingCMS />} />
            {/* Phase APP-4 — MHM packer approvals + MHM my-suggestions + Lead worker requests */}
            <Route path="hub/packer-approvals"        element={<MHMPackerApprovals />} />
            <Route path="hub/my-suggestions"          element={<MHMMySuggestions />} />
            <Route path="micro-hub/worker-requests"   element={<LeadWorkerRequests />} />
            {/* MHM redirect: legacy /dashboard/hub → new MHM portal landing. RoleGate on /hub/main blocks non-MHM with Forbidden403. */}
            <Route path="hub" element={<Navigate to="/dashboard/hub/main" replace />} />
            <Route path="hub/:hubId" element={<Navigate to="/dashboard/hub/main" replace />} />
            <Route path="microhub/:hubId" element={<MicroHubDashboard />} />
            {/* MHM-5.4 — legacy `/dashboard/microhub` redirect to the new portal.
                The old MicroHubDashboard route remains for MHM viewing another hub by id. */}
            <Route path="microhub" element={<Navigate to="/dashboard/micro-hub" replace />} />
            <Route path="ops" element={<OpsStaffDashboard />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="site-settings" element={<AdminSiteSettings />} />
            <Route path="serviceability" element={<AdminServiceability />} />
          </Route>
        </Routes>
        </OrderDrawerProvider>
      </BrowserRouter>
      </ThemeProvider>
      </RootErrorBoundary>
    </GoogleOAuthProvider>
  );
}

export default App;
